/*istanbul ignore file */
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import { useLoading } from "../../hooks/useLoader";
import Confirmation from "../../common/Confirmation/Confirmation";
import InlineMessage from "../../common/InlineMessage/InlineMessage";
import ApiServices from "../../services/ApiServices";
import {
  AD_FORMULA,
  AD_FORMULA_CHANGELOG,
} from "../../constants/api-constants";
import "./AdManagement.css";

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const rawHours = d.getHours();
  const ampm = rawHours >= 12 ? "PM" : "AM";
  const hh = String(rawHours % 12 || 12).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${min} ${ampm}`;
};

const formatLogValue = (value, fieldKey) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  const formatted = Number.isFinite(num) ? num.toFixed(2) : value;
  const isPriceField =
    fieldKey === "topBannerPricePerDay" ||
    fieldKey === "featuredBannerPricePerDay";
  return isPriceField ? `$${formatted}` : formatted;
};

/* ─── Field name display map ─────────────────────────────────────────────── */
const FIELD_LABEL_MAP = {
  topBannerPricePerDay: "Top Banner Price",
  featuredBannerPricePerDay: "Featured Banner Price",
  singleDeviceMultiplier: "Single Device",
  twoDeviceMultiplier: "Two Devices",
  allThreeDevices: "All three Devices",
  zeroFiltersMultiplier: "0 Filters",
  oneToTwoFiltersMultiplier: "1 to 2 Filters",
  threeToFiveFiltersMultiplier: "3 to 5 Filters",
  sixPlusFiltersMultiplier: "5+ Filters",
};

/* ─── Editable numeric field ─────────────────────────────────────────────── */
const CURRENCY_PATTERN = "^\\d*\\.?\\d{0,2}$";

const FormulaField = ({
  label,
  placeholder,
  fieldKey,
  value,
  prefix,
  suffix,
  mask,
  centerText,
  error,
  onChange,
  onBlur,
  touched,
}) => {
  const isCurrency = mask === "currency";
  const baseStyle = {
    textAlign: centerText ? "center" : "left",
    paddingLeft: "6px",
  };
  const fieldInputProps = isCurrency
    ? { inputMode: "decimal", pattern: CURRENCY_PATTERN, style: baseStyle }
    : { min: 0, step: "any", style: baseStyle };

  /* Caret preservation for currency:
   * A controlled input re-applies `value` on every render which resets the
   * caret to the end. We capture the caret in handleInput, translate it to
   * the post-mask position by counting kept chars, then restore it after
   * commit with useLayoutEffect. */
  const inputRef = useRef(null);
  const caretRef = useRef(null);

  useLayoutEffect(() => {
    if (!isCurrency || caretRef.current === null || !inputRef.current) return;
    const pos = caretRef.current;
    caretRef.current = null;
    try {
      inputRef.current.setSelectionRange(pos, pos);
    } catch {
      /* setSelectionRange unsupported for this input type — ignore. */
    }
  });

  const handleInput = (e) => {
    if (!isCurrency) {
      onChange(fieldKey, e.target.value);
      return;
    }
    const el = e.target;
    const raw = el.value;
    const selStart = el.selectionStart ?? raw.length;
    const masked = maskCurrency(raw);
    const keptBeforeCaret = raw
      .slice(0, selStart)
      .replaceAll(/[^\d.]/g, "").length;
    caretRef.current = Math.min(keptBeforeCaret, masked.length);
    onChange(fieldKey, masked);
  };

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Typography variant="body2" fontWeight={500} display="block" mb={0.5}>
        {label}
        <span style={{ color: "red", marginLeft: 2 }}>*</span>
      </Typography>
      <TextField
        fullWidth
        size="small"
        type={isCurrency ? "text" : "number"}
        placeholder={placeholder || ""}
        inputProps={fieldInputProps}
        inputRef={inputRef}
        value={value}
        onChange={handleInput}
        onBlur={() => onBlur(fieldKey)}
        sx={{
          "& input[type=number]": { MozAppearance: "textfield" },
          "& input[type=number]::-webkit-outer-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
          "& input[type=number]::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
        }}
        InputProps={{
          ...(prefix
            ? {
                startAdornment: (
                  <InputAdornment position="start">{prefix}</InputAdornment>
                ),
              }
            : {}),
          ...(suffix
            ? {
                endAdornment: (
                  <InputAdornment position="end">{suffix}</InputAdornment>
                ),
              }
            : {}),
        }}
      />
      <InlineMessage error={touched && error ? error : ""} />
    </Grid>
  );
};

FormulaField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  fieldKey: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  centerText: PropTypes.bool,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  touched: PropTypes.bool,
  mask: PropTypes.oneOf(["", "currency"]),
};
FormulaField.defaultProps = {
  placeholder: "",
  prefix: "",
  suffix: "",
  centerText: false,
  error: "",
  touched: false,
  mask: "",
};

/* ─── Validation ─────────────────────────────────────────────────────────── */
const validate = (values) => {
  const errors = {};
  const fields = [
    "topBannerPrice",
    "featuredBannerPrice",
    "singleDevice",
    "twoDevices",
    "allThreeDevices",
    "zeroFilters",
    "oneToTwoFilters",
    "threeToFiveFilters",
    "fivePlusFilters",
  ];
  fields.forEach((key) => {
    if (values[key] === "" || values[key] === undefined) {
      errors[key] = "This field is required.";
    } else if (Number.isNaN(Number(values[key])) || Number(values[key]) <= 0) {
      errors[key] = "Must be a positive number.";
    }
  });
  return errors;
};

/* Price fields that must accept at most 2 decimal places. */
const TWO_DECIMAL_FIELDS = new Set([
  "topBannerPrice",
  "featuredBannerPrice",
  "singleDevice",
  "twoDevices",
  "allThreeDevices",
  "zeroFilters",
  "oneToTwoFilters",
  "threeToFiveFilters",
  "fivePlusFilters",
]);

/* Currency input mask. Strips anything that isn't a digit or dot, keeps only
 * the first dot, and caps decimals at 2. Returns the raw masked string so
 * partial input like "", "1", or "1." remains usable while typing. */
const maskCurrency = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const cleaned = String(raw).replaceAll(/[^\d.]/g, "");
  const [whole, ...rest] = cleaned.split(".");
  if (rest.length === 0) return whole;
  return `${whole}.${rest.join("").slice(0, 2)}`;
};

/* Format a numeric/string value as a 2-decimal currency string ("12" → "12.00").
 * Returns "" for empty/non-numeric input so placeholders still render. */
const formatCurrency = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const n = Number(raw);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

/* ─── Initial state (replaced by API later) ──────────────────────────────── */
const INITIAL_VALUES = {
  topBannerPrice: "",
  featuredBannerPrice: "",
  singleDevice: "",
  twoDevices: "",
  allThreeDevices: "",
  zeroFilters: "",
  oneToTwoFilters: "",
  threeToFiveFilters: "",
  fivePlusFilters: "",
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const AdvBannersFormula = () => {

  const { showLoader } = useLoading();

  /* ── Hooks always before early returns ───────────────────────────────── */
  const [values, setValues] = useState({ ...INITIAL_VALUES });
  const [saved, setSaved] = useState({ ...INITIAL_VALUES });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [adFormulaId, setAdFormulaId] = useState(null);
  const [rawRecord, setRawRecord] = useState(null);
  const [changeLog, setChangeLog] = useState([]);

  // Fetch change log (can be called to refresh)
  const fetchChangeLog = () => {
    showLoader(true);
    ApiServices.httpGet(AD_FORMULA_CHANGELOG)
      .then((res) => {
        if (res?.responseStatus?.isSuccess && Array.isArray(res?.result)) {
          setChangeLog(res.result.slice(0, 100));
        }
      })
      .catch(() => {})
      .finally(() => showLoader(false));
  };

  useEffect(() => {
    showLoader(true);
    ApiServices.httpGet(AD_FORMULA)
      .then((res) => {
        if (res?.responseStatus?.isSuccess && res?.result) {
          const r = res.result;
          const mapped = {
            topBannerPrice: formatCurrency(r.topBannerPricePerDay),
            featuredBannerPrice: formatCurrency(r.featuredBannerPricePerDay),
            singleDevice: r.singleDeviceMultiplier ?? "",
            twoDevices: r.twoDeviceMultiplier ?? "",
            allThreeDevices: r.allDevicesMultiplier ?? "",
            zeroFilters: r.zeroFiltersMultiplier ?? "",
            oneToTwoFilters: r.oneToTwoFiltersMultiplier ?? "",
            threeToFiveFilters: r.threeToFiveFiltersMultiplier ?? "",
            fivePlusFilters: r.sixPlusFiltersMultiplier ?? "",
          };
          setValues(mapped);
          setSaved(mapped);
          setAdFormulaId(r.adFormulaId ?? null);
          setRawRecord(r);
        }
      })
      .catch(() => toast.error("Failed to load Advertisement Banner Formula."))
      .finally(() => showLoader(false));

    fetchChangeLog();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Handlers ────────────────────────────────────────────────────────── */
  const handleChange = (key, val) => {
    let next = val;
    if (TWO_DECIMAL_FIELDS.has(key)) {
      next = maskTwoDecimal(val);
    }
    setValues((prev) => ({ ...prev, [key]: next }));
    if (touched[key]) {
      setErrors(validate({ ...values, [key]: next }));
    }
  };
  // Mask for any field that should allow max 2 decimals
  // Mask for any field that should allow max 2 decimals (alternative implementation)
  const maskTwoDecimal = (raw) => {
    if (raw === "" || raw === null || raw === undefined) return "";
    // Remove all except digits and dot, but only keep the first dot
    let out = "";
    let dotSeen = false;
    for (let i = 0; i < String(raw).length; i++) {
      const c = String(raw)[i];
      if (c >= "0" && c <= "9") {
        out += c;
      } else if (c === "." && !dotSeen) {
        out += ".";
        dotSeen = true;
      }
    }
    // Limit to 2 decimals if dot present
    if (dotSeen) {
      const [whole, decimals] = out.split(".");
      return decimals !== undefined
        ? `${whole}.${decimals.slice(0, 2)}`
        : whole;
    }
    return out;
  };

  const handleBlur = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
    setValues((prev) => {
      if (!TWO_DECIMAL_FIELDS.has(key)) return prev;
      const raw = prev[key];
      const num = Number(raw);
      if (raw === "" || !Number.isFinite(num)) return prev;
      return { ...prev, [key]: num.toFixed(2) };
    });
    setErrors(validate(values));
  };

  const handleSaveClick = () => {
    const allTouched = Object.fromEntries(
      Object.keys(values).map((k) => [k, true]),
    );
    setTouched(allTouched);
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const hasChanges = Object.keys(values).some(
      (k) => String(values[k]) !== String(saved[k]),
    );
    if (!hasChanges) {
      toast.info("No changes detected.");
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    const payload = {
      ...(rawRecord ?? {}),
      adFormulaId,
      topBannerPricePerDay: Number(values.topBannerPrice),
      featuredBannerPricePerDay: Number(values.featuredBannerPrice),
      singleDeviceMultiplier: Number(values.singleDevice),
      twoDeviceMultiplier: Number(values.twoDevices),
      allDevicesMultiplier: Number(values.allThreeDevices),
      zeroFiltersMultiplier: Number(values.zeroFilters),
      oneToTwoFiltersMultiplier: Number(values.oneToTwoFilters),
      threeToFiveFiltersMultiplier: Number(values.threeToFiveFilters),
      sixPlusFiltersMultiplier: Number(values.fivePlusFilters),
    };
    ApiServices.httpPut(AD_FORMULA, payload)
      .then(() => {
        setSaved({ ...values });
        toast.success("Advertisement Banner Formula updated successfully.");
        // Reload change log after successful save
        showLoader(true);
        ApiServices.httpGet(AD_FORMULA_CHANGELOG)
          .then((res) => {
            if (res?.responseStatus?.isSuccess && Array.isArray(res?.result)) {
              setChangeLog(res.result.slice(0, 100));
            }
          })
          .catch(() => {})
          .finally(() => showLoader(false));
      })
      .catch(() => toast.error("Failed to save Advertisement Banner Formula."));
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setValues({ ...saved });
    setTouched({});
    setErrors({});
  };

  /* ── Field prop shorthand ────────────────────────────────────────────── */
  const fp = (key, placeholder, suffix) => ({
    fieldKey: key,
    value: values[key],
    placeholder,
    suffix,
    error: errors[key],
    touched: touched[key],
    onChange: handleChange,
    onBlur: handleBlur,
  });

  /* ── Render ──────────────────────────────────────────────────────────── */
  return (
    <Grid spacing={2} container className="module-container">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <Grid
        container
        item
        xs={12}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid item xs={10}>
          <Box pt={2} pr={4} pl={4} pb={0}>
            <h5>Advertisement Formula</h5>
          </Box>
        </Grid>
        <Grid
          item
          xs={2}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          pt={2}
          pr={4}
        >
          <Button
            variant="contained"
            color="primary"
            sx={{ minWidth: 90 }}
            onClick={handleSaveClick}
          >
            Save
          </Button>
        </Grid>
      </Grid>

      {/* ── Main card ────────────────────────────────────────────────── */}
      <Grid
        container
        component={Paper}
        sx={{ backgroundColor: "white" }}
        margin={5}
        paddingBottom={4}
      >
        <Grid
          container
          className="blue-field-section"
          marginLeft={5}
          marginRight={5}
          marginTop={5}
          marginBottom={2}
        >
          {/* Section 1: Banner Base Price */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#424242",
              }}
            >
              Banner Base Price
            </Typography>
            <Grid container spacing={3}>
              <FormulaField
                label="Top Banner Price"
                prefix="$"
                mask="currency"
                {...fp("topBannerPrice", "0.00", "/ Day")}
              />
              <FormulaField
                label="Featured Banner Price"
                prefix="$"
                mask="currency"
                {...fp("featuredBannerPrice", "0.00", "/ Day")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ width: "100%", mb: 3 }} />

          {/* Section 2: Device Multiplier */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#424242",
              }}
            >
              Device Multiplier
            </Typography>
            <Grid container spacing={3}>
              <FormulaField
                label="Single Device"
                mask="currency"
                {...fp("singleDevice", "0.00")}
              />
              <FormulaField
                label="Two Devices"
                mask="currency"
                {...fp("twoDevices", "0.00")}
              />
              <FormulaField
                label="All three Devices"
                mask="currency"
                {...fp("allThreeDevices", "0.00")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ width: "100%", mb: 3 }} />

          {/* Section 3: Targeting Filter Multiplier */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#424242",
              }}
            >
              Targeting Filter Multiplier
            </Typography>
            <Grid container spacing={3}>
              <FormulaField
                label="0 Filters"
                mask="currency"
                {...fp("zeroFilters", "0.00")}
              />
              <FormulaField
                label="1 to 2 Filters"
                mask="currency"
                {...fp("oneToTwoFilters", "0.00")}
              />
              <FormulaField
                label="3 to 5 Filters"
                mask="currency"
                {...fp("threeToFiveFilters", "0.00")}
              />
              <FormulaField
                label="5+ Filters"
                mask="currency"
                {...fp("fivePlusFilters", "0.00")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ width: "100%", mb: 3 }} />

          {/* Section 4: Change Log */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#424242",
              }}
            >
              Change Log
            </Typography>
            <ChangeLogTable changeLog={changeLog} />
          </Grid>

          <Divider sx={{ width: "100%", mb: 3 }} />

          {/* How Estimated Cost is Calculated */}
          <Grid item xs={12}>
            <Typography variant="body1" fontWeight={700} gutterBottom>
              How Estimated Cost is Calculated
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Base Rate{" "}
              <Box component="span" fontWeight={700}>
                ×
              </Box>{" "}
              Device Multiplier{" "}
              <Box component="span" fontWeight={700}>
                ×
              </Box>{" "}
              Number of Days{" "}
              <Box component="span" fontWeight={700}>
                ×
              </Box>{" "}
              Targeting Filter Multiplier
            </Typography>
            <Typography variant="body2" gutterBottom fontWeight={700} pt={2}>
              Definitions:
            </Typography>
            <Box component="ul" sx={{ mt: 0, pl: 2.5, "& li": { mb: 0.5 } }}>
              <li>
                <Typography variant="body2">
                  <strong>Base Price:</strong> Value entered for Top or Featured
                  Banner.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Device Multiplier:</strong> Based on selected device
                  types.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Number of Days:</strong> Total number of active days
                  (End Date – Start Date).
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>Targeting Filter Multiplier:</strong> Based on
                  targeting complexity.
                </Typography>
              </li>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* ── Confirmation modal ──────────────────────────────────────── */}
      <Confirmation
        open={confirmOpen}
        handleClose={handleCancel}
        title="Update Advertisement Formula"
        description="You are updating the system-wide Advertisement Formula. This will affect all future cost calculations for new and Draft advertisements. Do you want to proceed?"
        confirmButtonLabel="Proceed"
        cancelButtonLabel="Cancel"
        cancelButtonNotRequired={false}
        confirmButtonNotRequired={false}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Grid>
  );
};

export default AdvBannersFormula;

// Extracted Change Log Table for cognitive complexity reduction
const ChangeLogTable = ({ changeLog }) => {
  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
        <Table size="small" stickyHeader sx={{ marginTop: 0 }}>
          <TableHead>
            <TableRow>
              {[
                "Field Change",
                "Old Value",
                "New Value",
                "Updated by",
                "Last Updated",
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    backgroundColor: "#424242",
                    color: "#fff",
                    fontWeight: 600,
                    py: 1.2,
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {changeLog.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  align="center"
                  sx={{ py: 3, color: "text.secondary" }}
                >
                  No changes recorded yet.
                </TableCell>
              </TableRow>
            ) : (
              changeLog.map((row, i) => (
                <TableRow
                  key={`${row.fieldChanged}-${row.updatedOn}`}
                  sx={{
                    backgroundColor: i % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  <TableCell>
                    {FIELD_LABEL_MAP[row.fieldChanged] ??
                      FIELD_LABEL_MAP[
                        row.fieldChanged?.charAt(0).toLowerCase() +
                          row.fieldChanged?.slice(1)
                      ] ??
                      row.fieldChanged}
                  </TableCell>
                  <TableCell>
                    {formatLogValue(
                      row.oldValue,
                      row.fieldChanged?.charAt(0).toLowerCase() +
                        row.fieldChanged?.slice(1),
                    )}
                  </TableCell>
                  <TableCell>
                    {formatLogValue(
                      row.newValue,
                      row.fieldChanged?.charAt(0).toLowerCase() +
                        row.fieldChanged?.slice(1),
                    )}
                  </TableCell>
                  <TableCell>{row.updatedByName}</TableCell>
                  <TableCell>{formatDateTime(row.updatedOn)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

ChangeLogTable.propTypes = {
  changeLog: PropTypes.arrayOf(
    PropTypes.shape({
      fieldChanged: PropTypes.string,
      oldValue: PropTypes.any,
      newValue: PropTypes.any,
      updatedByName: PropTypes.string,
      updatedOn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
};
